import { MetricSummaryWidgets, PageWrapper } from '@repo/ui';
import {
  GetAllCoursesUserUseCaseInterface,
  GetAllCurricularUnitsUserUseCaseInterface,
  GetAllGroupsUserUseCaseInterface,
  GetAllOrganizationsUserUseCaseInterface,
  GetAllSemestersUserUseCaseInterface,
  GetAllUsersMetrics,
} from 'domain/usecases';
import { useFormik } from 'formik';
import { useAuth } from 'presentation/app/hooks/useAuth';
import { useLegends } from 'presentation/app/hooks/useLegends';
import { ActivitiesTabs } from 'presentation/app/modules/Dashboard/components/activities-tabs';
import { CardIndicators } from 'presentation/app/modules/Dashboard/components/card-indicators';
import { NavFilters } from 'presentation/app/modules/Dashboard/components/nav-filters';
import { OffcanvasFilters } from 'presentation/app/modules/Dashboard/components/offcanvas-filters';
import { WidgetsActivities } from 'presentation/app/modules/Dashboard/components/widgets-activities';
import { WidgetsCH } from 'presentation/app/modules/Dashboard/components/widgets-ch';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import { clearDashboardSummary } from 'presentation/config/store/dashboard/dashboard-get-summary.slice';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Col, Row } from 'reactstrap';
import * as Yup from 'yup';

import { DashboardMaps } from '../../components/dashboard-maps';
import { useDashboard } from '../../hooks/useDashboard';
import { IFiltersFormatted, useFilters } from '../../hooks/useFilters';

interface IProps {
  getAllUsersMetrics: GetAllUsersMetrics;
  getAllGroupsUser?: GetAllGroupsUserUseCaseInterface;
  getAllCoursesUser?: GetAllCoursesUserUseCaseInterface;
  getAllSemestersUser?: GetAllSemestersUserUseCaseInterface;
  getAllOrganizationsUser?: GetAllOrganizationsUserUseCaseInterface;
  getAllCurricularUnitsUser?: GetAllCurricularUnitsUserUseCaseInterface;
}
export const Dashboard = ({
  getAllGroupsUser,
  getAllCoursesUser,
  getAllSemestersUser,
  getAllOrganizationsUser,
  getAllCurricularUnitsUser,
}: IProps) => {
  // States
  const [isRight, setIsRight] = useState<boolean>(false);
  const [mapStatus, setMapStatus] = useState<string>('2');
  const [filtersDisabled, setFiltersDisabled] = useState<boolean>(true);

  const {
    groups: { groupsUser },
    courses: { coursesUser },
    semesters: { semestersUser },
    dashboard: {
      ch,
      users,
      completedAndMissedActivitiesCount,
      activitiesInProgressCount,
      activitiesByTask,
      activitiesByPlace,
      activitiesByGroup,
      activitiesByStudent,
      mapActivitiesMarkers,
      mapInternshipMarkers,
      mapActivityDetails,
    },
  } = useAppSelector((state) => ({
    groups: state.groups,
    courses: state.courses,
    semesters: state.semesters,
    dashboard: state.dashboard,
  }));

  // Hooks
  const dispatch = useDispatch();
  const { currentUser } = useAuth();
  const { getLegends } = useLegends();
  const { filters, saveFilters, filtersFormatted, clearFilters } = useFilters();

  const _controller = useDashboard({
    getAllGroupsUser,
    getAllCoursesUser,
    getAllSemestersUser,
    getAllOrganizationsUser,
    getAllCurricularUnitsUser,
  });
  // Constants
  const formatDate = (date: Date) => date.toISOString().slice(0, 10);

  const date = new Date();
  const currentDay = new Date();
  currentDay.setHours(0, 0, 0, 0);
  const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);

  let isNotAManager = Number(currentUser?.activeProfile?.profileId) !== 2;

  // Methods
  const toggleRightCanvas = () => {
    setIsRight(!isRight);
  };

  const updateMapStatus = (value: string) => {
    const filters = filtersFormatted();
    _controller.requestGetMapActivitiesMarkers({
      ...filters,
      status: value as '1' | '2' | '3',
    });
    _controller.requestGetMapInternshipMarkers({
      ...filters,
      status: value as '1' | '2' | '3',
    });
    setMapStatus(value);
  };

  const getAllDashboardData = useCallback(
    (filters: Partial<IFiltersFormatted>) => {
      Promise.all([
        _controller.requestGetAllCoursesUser(),
        _controller.requestGetAllOrganizationsUser(),
        _controller.requestGetSummaryProfile(filters),

        // indicators calls
        _controller.requestGetAllUsersMetrics(filters),
        _controller.requestGetCompletedAndMissedActivitiesCount(filters),
        _controller.requestGetActivitiesInProgressCount(filters),
        // _controller.requestGetAllActivitiesByStatus(filters),
        _controller.requestGetAllCHMetrics(filters),

        // card indicators calls (tables)
        _controller.requestGetAllActivitiesByPlace({ length: 5, ...filters }),
        _controller.requestGetAllActivitiesByTask({ length: 5, ...filters }),
        _controller.requestGetAllActivitiesByGroup({ length: 5, ...filters }),
        _controller.requestGetAllActivitiesByStudent({ length: 5, ...filters }),

        // map call
      ]);
    },
    [],
  );

  const getOptionallyFiltersData = useCallback((filters: IFiltersFormatted) => {
    if (!filters.semester)
      _controller.requestGetAllSemestersUser(filters?.entity);

    if (!filters.curricularUnit)
      _controller.requestGetAllCurricularUnitsUser({
        entityId: filters?.entity,
        courseId: filters?.course,
      });

    if (!filters.group)
      _controller.requestGetAllGroupsUser({
        courseId: filters?.course,
        semesterId: filters?.semester,
        entityId: filters?.entity,
        curricularUnitId: filters?.curricularUnit,
      });
  }, []);

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      entity: filters?.entity
        ? {
            label: filters?.entity?.label,
            value: filters?.entity?.value,
          }
        : null,
      course: filters?.course
        ? {
            label: filters?.course?.label,
            value: filters?.course?.value,
          }
        : null,
      semester: filters?.semester
        ? {
            label: filters?.semester?.label,
            value: filters?.semester?.value,
          }
        : null,
      unit: filters?.curricularUnit
        ? {
            label: filters?.curricularUnit?.label,
            value: filters?.curricularUnit?.value,
          }
        : null,
      group: filters?.group
        ? {
            label: filters?.group?.label,
            value: filters?.group?.value,
          }
        : null,
      period: filters?.period
        ? [filters?.period[0], filters?.period[1]]
        : [firstDayOfMonth, currentDay],
    },
    validationSchema: Yup.object({
      entity: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      course: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      semester: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      unit: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      group: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      period: Yup.array().required('Período é obrigatório'),
    }),

    onSubmit: (values) => {
      dispatch(clearDashboardSummary());
      // let period = formatRangePeriod(values?.period as any);
      const period = values.period.map(convertStringToDate);

      const valuesToSave = {
        entity: values.entity,
        course: values.course,
        semester: values.semester,
        curricularUnit: values.unit,
        group: values.group,
        period: values?.period,
        starts: period?.[0],
        ends: period?.[1],
      };

      saveFilters(valuesToSave as any);
      toggleRightCanvas();
    },
  });

  const handleChangeFilter = useCallback((values: IFiltersFormatted) => {
    if (values?.entity || isNotAManager) {
      getOptionallyFiltersData(values);
      setFiltersDisabled(false);
    } else {
      setFiltersDisabled(true);
    }
  }, []);

  const convertStringToDate = (dateString) => {
    if (typeof dateString === 'string') {
      const [day, month, year] = dateString.split('/').map(Number);
      return new Date(year, month - 1, day);
    }
    return dateString;
  };

  const requestFilterMaps = () => {
    const filters = filtersFormatted();
    _controller.requestGetMapActivitiesMarkers({
      ...filters,
      status: mapStatus as '1' | '2' | '3',
    });
    _controller.requestGetMapInternshipMarkers({
      ...filters,
      status: mapStatus as '1' | '2' | '3',
    });
  };

  useEffect(() => {
    const filtersFormatted2 = filtersFormatted();

    if (!filtersFormatted2) return;
    requestFilterMaps();

    getAllDashboardData(filtersFormatted2);
  }, [getAllDashboardData, filtersFormatted]);

  const { dashboardSummary } = useAppSelector(
    (state) => state.dashboardSummary,
  );

  const dataDashboardSummary = [
    {
      icon: {
        iconName: 'ri-user-2-fill',
        size: 30,
        classNameColorIcon: '#878A99',
      },
      label: 'Coordenadores',
      counter: new Intl.NumberFormat('pt-BR').format(
        dashboardSummary?.coordinators ?? 0,
      ),
    },

    {
      icon: {
        iconName: 'ri-user-star-fill',
        size: 30,
        classNameColorIcon: '#878A99',
      },
      label: 'Professores',
      counter: new Intl.NumberFormat('pt-BR').format(
        dashboardSummary?.teachers ?? 0,
      ),
    },

    {
      icon: {
        iconName: 'ri-user-star-fill',
        size: 30,
        classNameColorIcon: '#878A99',
      },
      label: 'Preceptores',
      counter: new Intl.NumberFormat('pt-BR').format(
        dashboardSummary?.preceptors ?? 0,
      ),
    },

    {
      icon: {
        iconName: 'ri-shield-user-fill',
        size: 30,
        classNameColorIcon: '#878A99',
      },
      label: 'Assistentes/Auxiliares',
      counter: new Intl.NumberFormat('pt-BR').format(
        dashboardSummary?.assistants ?? 0,
      ),
    },

    {
      icon: {
        iconName: 'ri-account-box-fill',
        size: 30,
        classNameColorIcon: '#878A99',
      },
      label: 'Estudantes',
      counter: new Intl.NumberFormat('pt-BR').format(
        dashboardSummary?.students ?? 0,
      ),
    },
  ];

  return (
    <PageWrapper title="Dashboard">
      <Row>
        <Col xs={12} md={12} lg={12}>
          <div className="d-flex align-content-center justify-content-between">
            <h4 className="mb-4">
              Visão Geral - <span className="fw-light">Dashboard</span>
            </h4>

            <button
              type="button"
              className="btn btn-soft-primary btn-icon waves-effect waves-light rounded-pill"
              onClick={toggleRightCanvas}
            >
              <i className="ri-filter-2-line fs-16"></i>
            </button>
          </div>

          <NavFilters
            filters={filters}
            lastDayOfMonth={formatDate(currentDay)}
            firstDayOfMonth={formatDate(firstDayOfMonth)}
          />

          <WidgetsCH statusCH={_controller.statusPage.statusCH} data={ch} />

          <WidgetsActivities
            users={users}
            completedAndMissedActivitiesCount={
              completedAndMissedActivitiesCount
            }
            activitiesInProgressCount={activitiesInProgressCount}
            statusCompletedAndMissedActivitiesCount={
              _controller.statusPage.statusCompletedAndMissedActivitiesCount
            }
            statusActivitiesInProgressCount={
              _controller.statusPage.statusActivitiesInProgressCount
            }
          />
          {String(currentUser?.activeProfile?.profileId) == '2' && (
            <MetricSummaryWidgets
              iconLib="remix"
              data={dataDashboardSummary}
              loading={_controller.loadingSummaryProfile}
            />
          )}

          <ActivitiesTabs />

          <Row>
            <CardIndicators
              data={activitiesByPlace}
              status={_controller.statusPage.statusActivitiesByPlace}
              title="Locais de estágio mais utilizados"
              noDataTitle="Nenhum local de estágio encontrado"
              noDataDescription="Não há locais de estágio cadastrados no momento."
            />
            <CardIndicators
              data={activitiesByTask}
              status={_controller.statusPage.statusActivitiesByTask}
              title="Tarefas mais utilizadas"
              noDataTitle="Nenhuma tarefa encontrada"
              noDataDescription="Não há tarefas cadastradas no momento."
            />
            <CardIndicators
              data={activitiesByStudent}
              status={_controller.statusPage.statusActivitiesByStudent}
              title={`${getLegends('ESTUDANTE')} mais efetivos`}
              isEffectiveStudentTable
              noDataTitle={`Nenhum ${getLegends('ESTUDANTE')} encontrado`}
              noDataDescription={`Não há ${getLegends(
                'ESTUDANTE',
              )}s cadastrados no momento.`}
            />
            <CardIndicators
              data={activitiesByGroup}
              status={_controller.statusPage.statusActivitiesByGroup}
              title="Grupos mais ativos"
              hasCodeColumn
              noDataTitle="Nenhum grupo encontrado"
              noDataDescription="Não há grupos cadastrados no momento."
            />
          </Row>

          <DashboardMaps
            updateMapStatus={updateMapStatus}
            mapActivityDetails={mapActivityDetails}
            mapActivitiesMarkers={mapActivitiesMarkers}
            mapInternshipMarkers={mapInternshipMarkers}
            requestGetMapActivityDetails={
              _controller.requestGetMapActivityDetails
            }
            loadingMapActivityDetails={
              _controller.statusPage.statusMapActivityDetails
            }
            loadingMapActivitiesMarkers={
              _controller.statusPage.statusMapActivitiesMarkers
            }
            loadingMapInternshipMarkers={
              _controller.statusPage.statusMapInternshipMarkers
            }
          />
        </Col>
      </Row>
      <OffcanvasFilters
        removeSessionAndClearFiltersSalved={clearFilters}
        isRight={isRight}
        validation={validation}
        groupsUser={groupsUser}
        coursesUser={coursesUser}
        toggle={toggleRightCanvas}
        semestersUser={semestersUser}
        organizationsUser={_controller.organizationsUser}
        curricularUnitsUser={_controller.curricularUnitsUser}
        requestGetAllCoursesUser={_controller.requestGetAllCoursesUser}
        showUnityFilter={currentUser?.filterByCurricularUnit}
        showSemesterFilter={currentUser?.filterBySemester}
        filtersDisabled={filtersDisabled}
        handleChangeFilter={handleChangeFilter}
      />
    </PageWrapper>
  );
};
